// client/src/components/pages/LexiStar/Approvals/CorridorMap.jsx
//
// Planned-versus-actual map for the approvals queue and trip detail, on the
// shared LexiStarMap (used read-only: declarative <Source>/<Layer>/<Marker>
// children, framing through the raw maplibre instance from onLoad).
//
// LS-09 §6: planned route a thin solid line; corridor a translucent band
// (toggle); actual track thicker; deviations magenta — approved dashed,
// unapproved solid; the truck a heading chevron in its state colour.
// Everything drawn here is also stated in the page's text (LS-09 §6: map
// content reachable via lists).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Source, Layer, Marker } from 'react-map-gl/maplibre';
import LexiStarMap from '../../../map/LexiStarMap';
import { boundsFromLngLats } from '../../../map/geo';
import { toFeature } from './useRouteGeometries';

const DEFAULT_VIEW = { longitude: 24.6727, latitude: -28.4793, zoom: 5 };
const PLANNED = '#2563eb';
const ACTUAL = '#334155';
const DEVIATION = '#d946ef';

const coordsOf = (geometry) => {
  if (!geometry) return [];
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates];
    case 'LineString':
      return geometry.coordinates;
    case 'Polygon':
    case 'MultiLineString':
      return geometry.coordinates.flat();
    case 'MultiPolygon':
      return geometry.coordinates.flat(2);
    default:
      return [];
  }
};

const fc = (features) => ({ type: 'FeatureCollection', features: features.filter(Boolean) });

export default function CorridorMap({
  idPrefix = 'lx',
  routes = [],
  corridors = [],
  track = null,
  deviations = [],
  position = null,
  positionColor = '#1e40af',
  stops = [],
  className = 'relative z-0 h-[360px] rounded-lg overflow-hidden border border-slate-200',
}) {
  const mapRef = useRef(null);
  const [showCorridor, setShowCorridor] = useState(true);

  const routeFC = useMemo(() => fc(routes.map((r) => r.feature)), [routes]);
  const corridorFC = useMemo(() => fc(corridors.map((c) => c.feature)), [corridors]);
  const trackFC = useMemo(() => fc([toFeature(track)]), [track]);
  const deviationFC = useMemo(
    () =>
      fc(
        deviations.map((d) => {
          const f = toFeature(d.geometry);
          return f
            ? { ...f, properties: { ...(f.properties || {}), approved: !!d.approved } }
            : null;
        })
      ),
    [deviations]
  );
  const stopsFC = useMemo(
    () =>
      fc(
        stops
          .filter((s) => s.lat != null && s.lon != null)
          .map((s) => ({
            type: 'Feature',
            properties: {},
            geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
          }))
      ),
    [stops]
  );

  const bounds = useMemo(() => {
    const pts = [
      ...routeFC.features.flatMap((f) => coordsOf(f.geometry)),
      ...trackFC.features.flatMap((f) => coordsOf(f.geometry)),
      ...deviationFC.features.flatMap((f) => coordsOf(f.geometry)),
      ...(position?.lon != null ? [[position.lon, position.lat]] : []),
    ];
    return boundsFromLngLats(pts);
  }, [routeFC, trackFC, deviationFC, position]);

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;
    try {
      map.fitBounds(bounds, { padding: 48, duration: 0, maxZoom: 15 });
    } catch {
      /* map not ready */
    }
  }, [bounds]);

  useEffect(() => {
    fit();
  }, [fit]);

  const onLoad = (e) => {
    mapRef.current = e.target;
    fit();
  };

  return (
    <div>
      <div className={className}>
        <LexiStarMap
          className="absolute inset-0"
          initialViewState={DEFAULT_VIEW}
          defaultBasemap="street"
          navigationPosition="top-left"
          onLoad={onLoad}
          overlay={
            corridorFC.features.length > 0 ? (
              <div className="absolute top-3 right-3 z-[1000]">
                <button
                  type="button"
                  onClick={() => setShowCorridor((v) => !v)}
                  aria-pressed={showCorridor}
                  className="bg-surface/95 backdrop-blur rounded-lg shadow-lg px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  Corridor {showCorridor ? 'on' : 'off'}
                </button>
              </div>
            ) : null
          }
        >
          {showCorridor && corridorFC.features.length > 0 && (
            <Source id={`${idPrefix}-corridor`} type="geojson" data={corridorFC}>
              <Layer
                id={`${idPrefix}-corridor-fill`}
                type="fill"
                paint={{ 'fill-color': PLANNED, 'fill-opacity': 0.14 }}
              />
            </Source>
          )}
          {routeFC.features.length > 0 && (
            <Source id={`${idPrefix}-planned`} type="geojson" data={routeFC}>
              <Layer
                id={`${idPrefix}-planned-line`}
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': PLANNED, 'line-width': 2.5 }}
              />
            </Source>
          )}
          {trackFC.features.length > 0 && (
            <Source id={`${idPrefix}-actual`} type="geojson" data={trackFC}>
              <Layer
                id={`${idPrefix}-actual-line`}
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': ACTUAL, 'line-width': 4.5, 'line-opacity': 0.85 }}
              />
            </Source>
          )}
          {deviationFC.features.length > 0 && (
            <Source id={`${idPrefix}-deviations`} type="geojson" data={deviationFC}>
              <Layer
                id={`${idPrefix}-deviation-unapproved`}
                type="line"
                filter={['!', ['get', 'approved']]}
                layout={{ 'line-cap': 'round' }}
                paint={{ 'line-color': DEVIATION, 'line-width': 5 }}
              />
              <Layer
                id={`${idPrefix}-deviation-approved`}
                type="line"
                filter={['get', 'approved']}
                paint={{ 'line-color': DEVIATION, 'line-width': 4, 'line-dasharray': [2, 1.5] }}
              />
            </Source>
          )}
          {stopsFC.features.length > 0 && (
            <Source id={`${idPrefix}-stops`} type="geojson" data={stopsFC}>
              <Layer
                id={`${idPrefix}-stops-circle`}
                type="circle"
                paint={{
                  'circle-radius': 5,
                  'circle-color': '#ffffff',
                  'circle-stroke-color': DEVIATION,
                  'circle-stroke-width': 2,
                }}
              />
            </Source>
          )}
          {position?.lon != null && position?.lat != null && (
            <Marker longitude={position.lon} latitude={position.lat}>
              <div
                title="Current position"
                style={{
                  width: 22,
                  height: 22,
                  transform: `rotate(${position.heading || 0}deg)`,
                  filter: 'drop-shadow(0 0 2px #fff)',
                }}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                  <path
                    d="M12 2 L20 21 L12 16 L4 21 Z"
                    fill={positionColor}
                    stroke="#fff"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
            </Marker>
          )}
        </LexiStarMap>
      </div>
      <ul
        className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 px-1 text-[11px] text-slate-500"
        aria-label="Map legend"
      >
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-5 h-0.5" style={{ background: PLANNED }} /> Planned
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-5 h-1" style={{ background: ACTUAL }} /> Actual
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-5 h-1" style={{ background: DEVIATION }} /> Unapproved
          deviation
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-5 h-0" style={{ borderTop: `3px dashed ${DEVIATION}` }} />{' '}
          Approved detour
        </li>
        {corridorFC.features.length > 0 && (
          <li className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-5 h-2.5 rounded-sm"
              style={{ background: `${PLANNED}33` }}
            />{' '}
            Corridor
          </li>
        )}
      </ul>
    </div>
  );
}
